import React from "react";
import t from "../../../../i18n";
import Text from "@ui/Text";
import Menu from "@ui/Menu";
import Option from "@ui/Option";
import Button from "@ui/Button";
import { ArrowDownSLine } from "@ui/IconV2/react";
import { colors, spacings } from "@ui/Theme";
import { css } from "@linaria/core";

export type DateSourceOption = {
  value: string;
  name: string;
  startDateLabel: string;
  startsAt: Date;
};

type Props = {
  cycleOptions: DateSourceOption[];
  setupOptions: DateSourceOption[];
  disabled: boolean;
  onCycleSelect: (option: DateSourceOption) => void;
  onSetupSelect: (option: DateSourceOption) => void;
};

export const FillStartDateFromCycleOrSetup = ({
  cycleOptions,
  setupOptions,
  disabled,
  onCycleSelect,
  onSetupSelect,
}: Props) => {
  const [cycleFilter, setCycleFilter] = React.useState("");
  const [setupFilter, setSetupFilter] = React.useState("");
  const filteredCycleOptions = React.useMemo(
    () =>
      cycleFilter.trim() === ""
        ? cycleOptions
        : cycleOptions.filter(option =>
            `${option.name} ${option.startDateLabel}`
              .toLowerCase()
              .includes(cycleFilter.trim().toLowerCase()),
          ),
    [cycleFilter, cycleOptions],
  );
  const filteredSetupOptions = React.useMemo(
    () =>
      setupFilter.trim() === ""
        ? setupOptions
        : setupOptions.filter(option =>
            `${option.name} ${option.startDateLabel}`
              .toLowerCase()
              .includes(setupFilter.trim().toLowerCase()),
          ),
    [setupFilter, setupOptions],
  );

  return (
    <div className={fillFromRow}>
      <Text type="body2" as="span">
        {t("project.settings.quantity.side_panel.section.formula_input.fill_from_prefix")}
      </Text>
      <Menu
        placement="bottom-start"
        filtered={cycleOptions.length > 5}
        filterValue={cycleFilter}
        onFilterChange={setCycleFilter}
        overlay={
          filteredCycleOptions.length > 0 ? (
            filteredCycleOptions.map(option => (
              <Option key={option.value} closeOnClick onClick={() => onCycleSelect(option)}>
                <div>
                  <Text type="body2">{option.name}</Text>
                  <Text type="body3" className={sourceOptionDate}>
                    {option.startDateLabel}
                  </Text>
                </div>
              </Option>
            ))
          ) : (
            <Option disabled>
              {t(
                "project.settings.quantity.side_panel.section.formula_input.fill_from_cycle_empty",
              )}
            </Option>
          )
        }
      >
        <Button variant="link" slim className={fillFromButton} disabled={disabled}>
          <span className={fillFromButtonContent}>
            <ArrowDownSLine size="s" />
            {t("project.settings.quantity.side_panel.section.formula_input.fill_from_cycle")}
          </span>
        </Button>
      </Menu>
      <Text type="body2" as="span">
        {t("project.settings.quantity.side_panel.section.formula_input.fill_from_separator")}
      </Text>
      <Menu
        placement="bottom-start"
        filtered={setupOptions.length > 5}
        filterValue={setupFilter}
        onFilterChange={setSetupFilter}
        overlay={
          filteredSetupOptions.length > 0 ? (
            filteredSetupOptions.map(option => (
              <Option key={option.value} closeOnClick onClick={() => onSetupSelect(option)}>
                <div>
                  <Text type="body2">{option.name}</Text>
                  <Text type="body3" className={sourceOptionDate}>
                    {option.startDateLabel}
                  </Text>
                </div>
              </Option>
            ))
          ) : (
            <Option disabled>
              {t(
                "project.settings.quantity.side_panel.section.formula_input.fill_from_setup_empty",
              )}
            </Option>
          )
        }
      >
        <Button variant="link" slim className={fillFromButton} disabled={disabled}>
          <span className={fillFromButtonContent}>
            <ArrowDownSLine size="s" />
            {t("project.settings.quantity.side_panel.section.formula_input.fill_from_setup")}
          </span>
        </Button>
      </Menu>
    </div>
  );
};

const fillFromRow = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xxs};
`;

const fillFromButton = css`
  display: inline-flex;
  align-items: center;
`;

const fillFromButtonContent = css`
  display: inline-flex;
  align-items: center;
  gap: ${spacings.xxs};
`;

const sourceOptionDate = css`
  color: ${colors.titanium500};
`;
